import type {
  ScoreBreakdown,
  VectorName,
  VectorScore,
} from '../../lib/artefacts-api';
import type { ArtefactsState } from '../../hooks/useBriefArtefacts';

/**
 * 4-vector breakdown table (Phase 5d-1 follow-up commit 5).
 *
 * Renders the static structure per ADR-031 §3 + fills the raw / weighted /
 * key-signals cells from score-breakdown.json when available. When the
 * artefact hasn't loaded yet (no agent run, still fetching, error) the cells
 * show "—" so the table stays usable as the staff-context surface.
 */

interface Props {
  state: ArtefactsState<ScoreBreakdown>;
  noAgentRunYet: boolean;
  strongFitBonusApplied: boolean | undefined;
  /** Fallback total score from the Brief — used when the score-breakdown
   *  artefact hasn't loaded yet. The Brief's score is the same value the
   *  agent wrote in the breakdown, so the total/subtotal columns stay
   *  accurate even before the per-vector data lands. */
  briefScore: number | null;
}

interface VectorRow {
  key: VectorName;
  label: string;
  weightLabel: string;
}

const ROWS: VectorRow[] = [
  { key: 'portfolioAuthenticity', label: 'Portfolio authenticity', weightLabel: '40 pts' },
  { key: 'matchQuality', label: 'Match quality', weightLabel: '25 pts' },
  { key: 'identityAuthenticity', label: 'Identity authenticity', weightLabel: '20 pts' },
  { key: 'webPresenceBreadth', label: 'Web presence breadth', weightLabel: '15 pts' },
];

export function ScoreBreakdownTable({
  state,
  noAgentRunYet,
  strongFitBonusApplied,
  briefScore,
}: Props) {
  const vectors = state.status === 'loaded' ? state.data?.vectors : undefined;
  const showBonus = strongFitBonusApplied === true;
  // Prefer the breakdown's score (full precision) when loaded; otherwise
  // fall back to the Brief record's score so the total row + subtotal stay
  // populated before the artefact lands.
  const total =
    state.status === 'loaded' && state.data?.score !== undefined
      ? state.data.score
      : briefScore;
  const baseTotal =
    total !== null && showBonus ? Math.max(0, total - 20) : total;

  return (
    <section className="portal-brief-section">
      <h2>Vector breakdown (Stage 2, ADR-031 §3)</h2>
      <table className="portal-vector-table">
        <thead>
          <tr>
            <th>Vector</th>
            <th className="right">Weight</th>
            <th className="right">Raw</th>
            <th className="right">Weighted</th>
            <th>Key signals</th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map((row) => (
            <VectorRowCells key={row.key} row={row} vector={vectors?.[row.key]} />
          ))}
          {showBonus && (
            <tr className="portal-vector-bonus">
              <td>+ Strong Fit bonus (Stage 1)</td>
              <td className="right">+20</td>
              <td className="right">+20</td>
              <td className="right">+20</td>
              <td>
                <span className="portal-vector-signal-key">strongFitBonusApplied</span>
              </td>
            </tr>
          )}
          <tr className="portal-vector-total">
            <td>
              {showBonus && baseTotal !== null
                ? `Subtotal ${baseTotal} · + 20 bonus →`
                : 'Total'}
            </td>
            <td className="right">100</td>
            <td className="right">—</td>
            <td className="right">{total ?? '—'}</td>
            <td>{state.data?.decision ?? ''}</td>
          </tr>
        </tbody>
      </table>
      {state.status === 'loading' && (
        <p className="portal-help portal-brief-help-note">Loading score breakdown…</p>
      )}
      {state.status === 'error' && (
        <p className="portal-help portal-brief-help-note">
          Could not load per-vector scores: {state.error}
        </p>
      )}
      {(state.status === 'no-agent-run-yet' || noAgentRunYet) && (
        <p className="portal-help portal-brief-help-note">
          Per-vector raw + weighted scores load from the S3{' '}
          <code>score-breakdown.json</code> artefact produced by each agent run. This Brief has not
          run the pow-agent yet — table structure mirrors ADR-031 §3 so staff can see the model
          the agent will score against.
        </p>
      )}
    </section>
  );
}

function VectorRowCells({ row, vector }: { row: VectorRow; vector?: VectorScore }) {
  if (!vector) {
    return (
      <tr>
        <td>{row.label}</td>
        <td className="right">{row.weightLabel}</td>
        <td className="right">—</td>
        <td className="right">—</td>
        <td>—</td>
      </tr>
    );
  }
  return (
    <tr>
      <td>{row.label}</td>
      <td className="right">{row.weightLabel}</td>
      <td className="right">{Math.round(vector.raw)}</td>
      <td className="right">{Math.round(vector.weighted)}</td>
      <td>
        <SignalsList signals={vector.signals} />
      </td>
    </tr>
  );
}

function SignalsList({ signals }: { signals: Record<string, unknown> }) {
  const entries = Object.entries(signals);
  if (entries.length === 0) return <span>—</span>;
  return (
    <ul className="portal-vector-signals-list">
      {entries.map(([k, v]) => {
        const text = formatSignalValue(v);
        const polarity = polarityOf(v, text);
        // 30 May 2026 — when the value is a complete descriptive sentence
        // (multi-word + punctuation) the agent has already given the
        // operator enough context; we drop the field name to reduce
        // clutter. When the value is raw (boolean, number, short coded
        // string) the field name carries the meaning, so we render it as
        // a humanised bold label. UAT iteration corrected an earlier
        // overcorrection that stripped the field name universally and
        // left raw "✓" / "20" / ".com" bullets with no context.
        const isDescriptiveSentence =
          typeof v === 'string' && /\s/.test(text) && text.length > 30;
        return (
          <li
            key={k}
            className={`portal-vector-signal-item portal-vector-signal-item-${polarity}`}
            // Keep the machine field name available on hover for ops + debug
            // regardless of whether we render it inline.
            title={k}
          >
            {isDescriptiveSentence ? (
              text
            ) : (
              <>
                <strong className="portal-vector-signal-label">{humaniseFieldName(k)}:</strong>{' '}
                {text}
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * camelCase → "Camel case". Splits before each capital letter, lowercases
 * the rest, capitalises the first character. Acronym runs (e.g. "ABN",
 * "TLD") are preserved by detecting uppercase clusters.
 */
function humaniseFieldName(k: string): string {
  if (!k) return '';
  // Insert spaces before uppercase letters that follow lowercase letters
  // ("camelCase" → "camel Case") or before the trailing letter of a run
  // of uppercase before lowercase ("XMLHttp" → "XML Http").
  const spaced = k
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
  // Lower-case everything after the first character so it reads like a
  // normal sentence cap. Keep all-caps acronyms uppercase when they sit
  // alone as a word (e.g. "ABN", "TLD", "ABR").
  const words = spaced.split(/\s+/);
  return words
    .map((w, i) => {
      if (/^[A-Z]{2,}$/.test(w)) return w; // acronym
      return i === 0 ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w.toLowerCase();
    })
    .join(' ');
}

function formatSignalValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? '✓' : '✕';
  if (typeof v === 'number' || typeof v === 'string') return String(v);
  if (Array.isArray(v)) {
    return v.length === 0 ? '[]' : `[${v.length}]`;
  }
  return JSON.stringify(v);
}

type SignalPolarity = 'negative' | 'neutral' | 'positive';

/**
 * Heuristic: classify a signal as negative / neutral / positive so the
 * staff portal can colour-code blockers. The Stage 2 agent doesn't emit
 * an explicit polarity field today, so we pattern-match the value text.
 * Two flavours of negative:
 *   - Natural-language: "No ...", "Not confirmed", "unavailable",
 *     "missing", "could not", "doesn't apply", etc.
 *   - Underscore-cased machine codes: `no_*`, `not_*`, `skipped_*`,
 *     `missing_*`, `failed_*`, `absent_*`, `timed_out_*` — agent emits
 *     these for short-circuit / probe-failure signals where a full
 *     sentence would be overkill.
 * Tightened 30 May 2026 per operator UAT: previously underscore-coded
 * negatives like `skipped_no_registration_number` fell through to
 * neutral and ended up amber instead of red.
 */
function polarityOf(raw: unknown, text: string): SignalPolarity {
  if (typeof raw === 'boolean') return raw ? 'positive' : 'negative';
  if (text === '✓') return 'positive';
  if (text === '✕') return 'negative';
  const lower = text.toLowerCase();
  if (
    /^no\b/.test(lower) ||
    /\bnot confirmed\b/.test(lower) ||
    /\bnot apply\b/.test(lower) ||
    /\bcould not\b/.test(lower) ||
    /\bcouldn['’]t\b/.test(lower) ||
    /\bunavailable\b/.test(lower) ||
    /\bmissing\b/.test(lower) ||
    /\bdoes ?n['’]?t (apply|exist)\b/.test(lower) ||
    /\bnone (found|detected)\b/.test(lower) ||
    /\bzero (verifiable|confirmed)\b/.test(lower) ||
    /\b(identity|evidence) is thin\b/.test(lower) ||
    // Underscore-cased machine codes
    /^(no|not|skipped|missing|failed|absent)_/.test(lower)
  ) {
    return 'negative';
  }
  return 'neutral';
}
